import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MAX_TELEFONOS_UNIDAD } from '@kodica/shared';

import { Iconos } from '../componentes/iconos';
import { MapaUbicacion } from '../componentes/MapaUbicacion';
import {
  Boton,
  Campo,
  Cargando,
  Chip,
  Encabezado,
  Entrada,
  ErrorCaja,
  Panel,
  Vacio,
} from '../componentes/ui';
import { ErrorApi, pedir } from '../lib/api';
import { useRecurso } from '../lib/consultas';
import { useSesion } from '../lib/sesion';
import { ModalAlta, type Pestana } from './padron';

/**
 * Todo lo de una vivienda, en una pantalla.
 *
 * Antes esto estaba repartido en cuatro pestañas del padrón: para atender un
 * reclamo de la A-14 había que buscarla cuatro veces, una por pestaña, y
 * acordarse de qué se había visto en cada una. El API ya devolvía todo junto en
 * `GET /padron/unidades/:id` —residentes, vehículos y credenciales en un solo
 * include— y la interfaz nunca lo llamaba.
 *
 * Es una **ruta y no un modal**: hay que poder mandarle el enlace a alguien y
 * hay que poder volver atrás con el botón del navegador.
 */

interface Telefono {
  id: string;
  numero: string;
  etiqueta: string | null;
  orden: number;
}

interface DetalleUnidad {
  id: string;
  clave: string;
  tipo: string;
  activa: boolean;
  notas: string | null;
  calle: string | null;
  numero: string | null;
  ciudad: string | null;
  municipio: string | null;
  codigoPostal: string | null;
  lat: number | null;
  lng: number | null;
  zona: { nombre: string } | null;
  residentes: {
    id: string;
    tipo: string;
    puedeAutorizar: boolean;
    telefonosContacto: string[];
    usuario: {
      nombre: string;
      apellidoPaterno: string | null;
      apellidoMaterno: string | null;
      activo: boolean;
    };
  }[];
  vehiculos: { id: string; placa: string; marca: string | null; color: string | null }[];
  tags: { id: string; codigo: string; tipo: string; estado: string }[];
  telefonos: Telefono[];
}

export function UnidadPagina() {
  const { id = '' } = useParams();
  const navegar = useNavigate();
  const { datos, cargando, error, recargar } = useRecurso<DetalleUnidad>(
    id ? `/padron/unidades/${id}` : null,
  );

  /** Qué se está dando de alta, o `null`. Siempre para **esta** vivienda. */
  const [alta, setAlta] = useState<Pestana | null>(null);
  /**
   * Lo tecleado en la credencial cuando se sale a crear un vehículo. Su
   * presencia es lo que marca que el alta de vehículo es un desvío y no un fin
   * en sí mismo: al guardarlo se vuelve aquí en vez de cerrar.
   */
  const [borradorTag, setBorradorTag] = useState<Record<string, string> | undefined>();
  /**
   * El vehículo recién creado en ese desvío. Se mete en la lista a mano porque
   * `recargar()` no se puede esperar: sin esto se vuelve a la credencial con un
   * vehículo elegido que todavía no está en el desplegable, y el campo se ve
   * vacío justo después de haberlo creado.
   */
  const [vehiculoNuevo, setVehiculoNuevo] = useState<{ id: string; placa: string } | null>(null);
  /**
   * Si el alta de vehículo abierta es un desvío. Va en una referencia y no en
   * el estado porque `enviar` captura estas funciones al empezar el POST y las
   * llama al terminar: leer el estado de entonces haría que cancelar a media
   * petición reabriera el formulario que se acaba de cerrar.
   */
  const desvio = useRef(false);

  // Identidad estable sobre **los campos**, no sobre `datos`: cada recarga trae
  // un objeto nuevo, y como esto es dependencia del efecto que rellena el
  // formulario, bastaría con eso para borrar lo tecleado a media captura.
  const unidadId = datos?.id;
  const clave = datos?.clave;
  const calle = datos?.calle;
  const numeroCalle = datos?.numero;
  const unidadFija = useMemo(
    () =>
      unidadId && clave !== undefined
        ? { id: unidadId, clave, calle: calle ?? null, numero: numeroCalle ?? null }
        : undefined,
    [unidadId, clave, calle, numeroCalle],
  );

  function abrir(que: Pestana) {
    desvio.current = false;
    setBorradorTag(undefined);
    setVehiculoNuevo(null);
    setAlta(que);
  }

  function cerrar() {
    desvio.current = false;
    setAlta(null);
    setBorradorTag(undefined);
    setVehiculoNuevo(null);
  }

  /** Cancelar un desvío **regresa a la credencial**, no tira todo. Quien salió
   *  a dar de alta un coche y se arrepiente no perdió el código que ya tecleó. */
  function cancelar() {
    if (desvio.current) {
      desvio.current = false;
      setAlta('tags');
      return;
    }
    cerrar();
  }

  function guardado(creado?: Record<string, unknown>) {
    // Volver del desvío: el vehículo recién creado queda elegido en la
    // credencial, con lo que ya se había tecleado.
    if (desvio.current && creado) {
      desvio.current = false;
      const idNuevo = creado['id'] as string;
      setVehiculoNuevo({ id: idNuevo, placa: String(creado['placa'] ?? '') });
      setBorradorTag((b) => ({ ...b, vehiculoId: idNuevo }));
      setAlta('tags');
      recargar();
      return;
    }
    cerrar();
    recargar();
  }

  // Solo mientras no hay nada que enseñar. Recargar **con el modal abierto** es
  // lo normal aquí —cada alta recarga la ficha—, y cortar el árbol entero por
  // eso lo desmontaría: la pantalla parpadearía a «Cargando» en mitad de una
  // captura, y si esa recarga falla se pierde lo tecleado sin vuelta atrás.
  if (cargando && !datos) return <Cargando />;
  if (error && !datos) return <ErrorCaja>{error}</ErrorCaja>;
  if (!datos) return <Vacio>No se encontró la vivienda.</Vacio>;

  const domicilio = [datos.calle, datos.numero, datos.ciudad, datos.municipio]
    .filter(Boolean)
    .join(' ');

  // Los titulares primero: son quienes responden por la vivienda y a quienes se
  // llama. Verlos mezclados con los habitantes obliga a leer los chips uno por
  // uno para encontrar a quién avisarle.
  const personas = [...datos.residentes].sort((a, b) =>
    a.tipo === b.tipo ? 0 : a.tipo === 'TITULAR' ? -1 : 1,
  );

  return (
    <>
      <div className="mb-stack-md">
        <Boton variante="texto" icono={Iconos.anterior} onClick={() => navegar('/padron')}>
          Padrón
        </Boton>
      </div>

      <Encabezado
        titulo={datos.clave}
        descripcion={
          [datos.tipo.toLowerCase(), datos.zona?.nombre, domicilio].filter(Boolean).join(' · ') ||
          undefined
        }
      >
        {!datos.activa && <Chip tono="neutro">Dada de baja</Chip>}
      </Encabezado>

      <div className="grid gap-stack-md lg:grid-cols-2">
        <Seccion
          titulo="Personas"
          vacio="Nadie vive aquí todavía."
          hay={personas.length > 0}
          alAgregar={() => abrir('residentes')}
        >
          {personas.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 px-5 py-3">
              <div className="min-w-0">
                <p className="truncate text-body-md text-on-surface">
                  {r.usuario.nombre} {r.usuario.apellidoPaterno ?? ''}{' '}
                  {r.usuario.apellidoMaterno ?? ''}
                </p>
                <p className="text-label-sm text-on-surface-variant">
                  {r.tipo === 'TITULAR' ? 'Titular' : 'Habitante'}
                  {r.puedeAutorizar ? ' · genera códigos' : ''}
                </p>
              </div>
              <Chip tono={r.usuario.activo ? 'ok' : 'neutro'}>
                {r.usuario.activo ? 'Activo' : 'Baja'}
              </Chip>
            </div>
          ))}
        </Seccion>

        <TelefonosDeLaVivienda
          unidadId={datos.id}
          telefonos={datos.telefonos}
          alGuardar={recargar}
        />

        <Seccion
          titulo="Vehículos"
          vacio="Sin vehículos registrados."
          hay={datos.vehiculos.length > 0}
          alAgregar={() => abrir('vehiculos')}
        >
          {datos.vehiculos.map((v) => (
            <div key={v.id} className="flex items-center justify-between gap-3 px-5 py-3">
              <p className="font-mono text-label-bold text-on-surface">{v.placa}</p>
              <p className="text-body-md text-on-surface-variant">
                {[v.marca, v.color].filter(Boolean).join(' · ') || '—'}
              </p>
            </div>
          ))}
        </Seccion>

        <Seccion
          titulo="Credenciales"
          vacio="Sin credenciales entregadas."
          hay={datos.tags.length > 0}
          alAgregar={() => abrir('tags')}
        >
          {datos.tags.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 px-5 py-3">
              <p className="font-mono text-label-bold text-on-surface">{t.codigo}</p>
              <Chip tono={t.estado === 'ACTIVO' ? 'ok' : 'aviso'}>{t.estado.toLowerCase()}</Chip>
            </div>
          ))}
        </Seccion>
      </div>

      {datos.lat != null && datos.lng != null && (
        <div className="mt-stack-md">
          <Panel>
            <div className="border-b border-outline-variant px-5 py-3">
              <h2 className="text-label-bold uppercase tracking-wider text-on-surface-variant">
                Dónde está
              </h2>
            </div>
            <div className="p-5">
              <MapaUbicacion lat={datos.lat} lng={datos.lng} />
            </div>
          </Panel>
        </div>
      )}

      {datos.notas && (
        <div className="mt-stack-md">
          <Panel>
            <div className="p-5">
              <p className="mb-1 text-label-bold uppercase tracking-wider text-on-surface-variant">
                Notas
              </p>
              <p className="text-body-md text-on-surface">{datos.notas}</p>
            </div>
          </Panel>
        </div>
      )}

      {/* El mismo formulario del padrón, con la vivienda ya puesta. Es el de
          allá y no una copia: dos pantallas gemelas se separan con el primer
          campo que se agregue en una sola. */}
      <ModalAlta
        pestana={alta ?? 'residentes'}
        abierto={alta !== null}
        registro={null}
        unidadFija={unidadFija}
        camposIniciales={borradorTag}
        vehiculos={
          alta === 'tags'
            ? [
                ...datos.vehiculos.map((v) => ({ id: v.id, placa: v.placa })),
                ...(vehiculoNuevo && !datos.vehiculos.some((v) => v.id === vehiculoNuevo.id)
                  ? [vehiculoNuevo]
                  : []),
              ]
            : undefined
        }
        alPedirVehiculo={(campos) => {
          desvio.current = true;
          setBorradorTag(campos);
          setAlta('vehiculos');
        }}
        alCerrar={cancelar}
        alGuardar={guardado}
      />
    </>
  );
}

function Seccion({
  titulo,
  vacio,
  hay,
  alAgregar,
  children,
}: {
  titulo: string;
  vacio: string;
  hay: boolean;
  /** Da de alta uno **de esta vivienda**, sin volver a buscarla. */
  alAgregar?: () => void;
  children: React.ReactNode;
}) {
  return (
    <Panel>
      <div className="flex items-center justify-between gap-3 border-b border-outline-variant px-5 py-3">
        <h2 className="text-label-bold uppercase tracking-wider text-on-surface-variant">
          {titulo}
        </h2>
        {alAgregar && (
          <Boton variante="texto" icono={Iconos.agregar} onClick={alAgregar}>
            Agregar
          </Boton>
        )}
      </div>
      {hay ? (
        <div className="divide-y divide-outline-variant">{children}</div>
      ) : (
        <p className="p-5 text-body-md text-on-surface-variant">{vacio}</p>
      )}
    </Panel>
  );
}

/**
 * A quién llama la caseta, y **en qué orden**.
 *
 * El orden no es un detalle: el guardia oprime «Llamar» una vez y el servidor
 * marca el 1, y si no contestan pasa al 2. Por eso los números van numerados a
 * la vista y se pueden mover — no es una lista, es una cascada.
 */
function TelefonosDeLaVivienda({
  unidadId,
  telefonos,
  alGuardar,
}: {
  unidadId: string;
  telefonos: Telefono[];
  alGuardar: () => void;
}) {
  // `TelefonoUnidad` baja de la nube y no sube: editarlo en la caseta se
  // revertiría en la siguiente sincronización, y desde hoy la ruta ni siquiera
  // existe ahí. Se enseña la cascada —el guardia necesita ver a quién se llama—
  // pero sin poder tocarla. Ver la nota larga en `padron.tsx`.
  const { rolServidor } = useSesion();
  const puedeEditar = rolServidor === 'nube';
  const [lista, setLista] = useState(() =>
    telefonos.map((t) => ({ numero: t.numero, etiqueta: t.etiqueta ?? '' })),
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function mover(desde: number, hacia: number) {
    if (hacia < 0 || hacia >= lista.length) return;
    const copia = [...lista];
    const [sacado] = copia.splice(desde, 1);
    copia.splice(hacia, 0, sacado!);
    setLista(copia);
  }

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      await pedir(`/padron/unidades/${unidadId}/telefonos`, {
        metodo: 'PUT',
        cuerpo: {
          telefonos: lista
            .filter((t) => t.numero.trim())
            .map((t) => ({ numero: t.numero.trim(), etiqueta: t.etiqueta.trim() || null })),
        },
      });
      alGuardar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudieron guardar');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Panel>
      <div className="border-b border-outline-variant px-5 py-3">
        <h2 className="text-label-bold uppercase tracking-wider text-on-surface-variant">
          Teléfonos de la vivienda
        </h2>
        <p className="mt-1 text-label-sm text-on-surface-variant">
          La caseta los marca en este orden hasta que alguien contesta.
        </p>
      </div>

      <div className="space-y-3 p-5">
        {error && <ErrorCaja>{error}</ErrorCaja>}

        {/* Lo que de verdad importa decir: sin números, el botón «Llamar» del
            guardia no puede hacer nada, y él lo descubre con el visitante
            enfrente. */}
        {lista.length === 0 && (
          <p className="rounded-lg bg-tertiary-container/20 px-4 py-3 text-body-md text-on-surface">
            La caseta no puede llamar a esta vivienda.
          </p>
        )}

        {lista.map((t, i) => (
          <div key={i} className="flex items-end gap-2">
            <span className="mb-3 w-5 text-center text-headline-md text-primary">{i + 1}</span>
            <div className="flex-1">
              <Campo etiqueta={i === 0 ? 'Número' : ''}>
                <Entrada
                  type="tel"
                  value={t.numero}
                  onChange={(e) =>
                    setLista(lista.map((x, k) => (k === i ? { ...x, numero: e.target.value } : x)))
                  }
                  placeholder="33 1234 5678"
                />
              </Campo>
            </div>
            <div className="w-32">
              <Campo etiqueta={i === 0 ? 'Etiqueta' : ''}>
                <Entrada
                  value={t.etiqueta}
                  onChange={(e) =>
                    setLista(
                      lista.map((x, k) => (k === i ? { ...x, etiqueta: e.target.value } : x)),
                    )
                  }
                  placeholder="Casa"
                />
              </Campo>
            </div>
            {/* Botones de 44px: esto también se toca desde una tablet. */}
            <Boton
              variante="texto"
              aria-label="Subir"
              onClick={() => mover(i, i - 1)}
              disabled={i === 0}
            >
              ↑
            </Boton>
            <Boton
              variante="texto"
              aria-label="Bajar"
              onClick={() => mover(i, i + 1)}
              disabled={i === lista.length - 1}
            >
              ↓
            </Boton>
            <Boton
              variante="texto"
              aria-label="Quitar"
              onClick={() => setLista(lista.filter((_, k) => k !== i))}
            >
              Quitar
            </Boton>
          </div>
        ))}

        {puedeEditar ? (
          <div className="flex gap-3 pt-1">
            {lista.length < MAX_TELEFONOS_UNIDAD && (
              <Boton
                variante="contorno"
                icono={Iconos.agregar}
                onClick={() => setLista([...lista, { numero: '', etiqueta: '' }])}
              >
                Agregar número
              </Boton>
            )}
            <Boton variante="primario" disabled={guardando} onClick={() => void guardar()}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </Boton>
          </div>
        ) : (
          <p className="pt-1 text-sm text-tenue">
            La cascada se edita en la nube, que es de donde baja.
          </p>
        )}
      </div>
    </Panel>
  );
}
